import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Database, 
  FileText, 
  Eye,
  ArrowLeft,
  Play,
  Loader2,
  RefreshCw,
  X,
  Edit2,
  Save,
  Download,
  DownloadCloud,
  CheckCircle,
  Code,
  BarChart3,
  Key,
  Link2,
  Lightbulb,
  ChevronDown,
  Sparkles,
  MessageSquare,
  Upload,
  Server
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNotification } from '../context/NotificationContext';
import { apiService } from '../services/apiService';
import ProcessLoadingScreen from '../components/ProcessLoadingScreen';

interface ColumnEDA {
  column_name: string;
  data_type: string;
  total_count: number;
  non_null_count: number;
  null_count: number;
  null_percentage: number;
  unique_count: number;
  unique_percentage: number;
  is_numeric: boolean;
  min?: number;
  max?: number;
  mean?: number;
  median?: number;
  std?: number;
  variance?: number;
  mode?: (string | number)[];
  percentiles?: {
    p25: number;
    p50: number;
    p75: number;
    p95: number;
    p99: number;
  };
  skewness?: number;
  kurtosis?: number;
  histogram?: {
    counts: number[];
    bin_edges: number[];
  };
  top_values?: Array<{value: string; count: number; percentage: number}>;
}

const GeneratePage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const { showSuccess, showError } = useNotification();
  
  // State
  const [runData, setRunData] = useState<any>(null);
  const [numRows, setNumRows] = useState('500');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  
  // Business rules
  const [constraints, setConstraints] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [applyStep, setApplyStep] = useState(0);
  
  // Preview/Edit with pagination
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewFilename, setPreviewFilename] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<any[]>([]);
  const [editingCell, setEditingCell] = useState<{rowIndex: number, column: string} | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  
  // Export to database
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStep, setExportStep] = useState(0);
  const [exportDbType, setExportDbType] = useState<'mysql' | 'postgresql' | 'oracle' | 'sqlserver'>('mysql');
  const [exportConnection, setExportConnection] = useState({
    type: 'mysql' as 'mysql' | 'postgresql' | 'oracle' | 'sqlserver',
    host: 'localhost',
    port: '3306',
    database: '',
    username: '',
    password: '',
    schema: ''
  });
  const [exportConnectionStatus, setExportConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  
  // Code viewer
  const [showCodeViewer, setShowCodeViewer] = useState(false);
  const [generatorCode, setGeneratorCode] = useState<string>('');
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  
  // Column EDA
  const [showEDAModal, setShowEDAModal] = useState(false);
  const [columnEDA, setColumnEDA] = useState<ColumnEDA | null>(null);
  const [isLoadingEDA, setIsLoadingEDA] = useState(false);
  const [edaTableName, setEdaTableName] = useState('');

  // Business rules suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<any>(null);
  const [cachedSuggestions, setCachedSuggestions] = useState<any>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [dataPurpose, setDataPurpose] = useState('');
  const [showPurposeInput, setShowPurposeInput] = useState(false);
  const [isPurposeLocked, setIsPurposeLocked] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [activeSuggestionTab, setActiveSuggestionTab] = useState<string>('all');

  useEffect(() => {
    if (runId) {
      loadRunData();
    }
  }, [runId]);

  const loadRunData = async () => {
    if (!runId) return;
    
    try {
      const data = await apiService.getRun(runId);
      setRunData(data);
      dispatch({ type: 'SET_CURRENT_RUN', payload: data });
    } catch (error) {
      console.error('Failed to load run data:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to load run data');
    }
  };

  // Business Rules
  const handleApplyRules = async () => {
    if (!runId || !constraints.trim()) {
      showError('Error', 'Please enter business rules');
      return;
    }

    setIsApplying(true);
    setApplyStep(0);
    
    const stepInterval = setInterval(() => {
      setApplyStep(prev => (prev + 1) % 5);
    }, 1000);
    
    try {
      const result = await apiService.applyBusinessRules(runId, constraints);
      
      clearInterval(stepInterval);
      
      if (result && result.success) {
        showSuccess('Success', result.message || 'Business rules applied successfully');
      } else {
        showError('Error', result?.error || 'Failed to apply business rules');
      }
    } catch (error) {
      clearInterval(stepInterval);
      showError('Error', error instanceof Error ? error.message : 'Failed to apply business rules');
    } finally {
      setIsApplying(false);
    }
  };

  // Load business rules suggestions (cached)
  const handleLoadSuggestions = async (purpose?: string, forceRefresh: boolean = false) => {
    if (!runId) return;
    
    // Use cached suggestions if available and not forcing refresh
    if (cachedSuggestions && !forceRefresh) {
      setSuggestions(cachedSuggestions);
      setShowSuggestions(true);
      return;
    }
    
    setIsLoadingSuggestions(true);
    
    try {
      const result = await apiService.getBusinessRulesSuggestions(runId, purpose);
      setSuggestions(result);
      setCachedSuggestions(result); // Cache the result
      setShowSuggestions(true);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to load suggestions');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Add suggestion to constraints
  const addSuggestion = (prompt: string) => {
    if (constraints) {
      setConstraints(prev => prev + '\n' + prompt);
    } else {
      setConstraints(prompt);
    }
  };

  // Add multiple selected suggestions
  const addSelectedSuggestions = () => {
    if (selectedSuggestions.size === 0) return;
    
    const prompts: string[] = [];
    
    // Collect prompts from context suggestions
    if (suggestions?.context_suggestions) {
      suggestions.context_suggestions.forEach((s: any, i: number) => {
        if (selectedSuggestions.has(i)) {
          prompts.push(s.prompt);
        }
      });
    }
    
    // Collect prompts from category suggestions
    if (suggestions?.suggestions) {
      let indexOffset = suggestions.context_suggestions?.length || 0;
      suggestions.suggestions.forEach((category: any) => {
        category.suggestions.forEach((prompt: string, j: number) => {
          if (selectedSuggestions.has(indexOffset + j)) {
            prompts.push(prompt);
          }
        });
        indexOffset += category.suggestions.length;
      });
    }
    
    if (prompts.length > 0) {
      const newRules = prompts.join('\n');
      if (constraints) {
        setConstraints(prev => prev + '\n' + newRules);
      } else {
        setConstraints(newRules);
      }
      setSelectedSuggestions(new Set());
      setShowSuggestions(false);
    }
  };

  // Toggle suggestion selection
  const toggleSuggestion = (index: number) => {
    setSelectedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Generate Data
  const handleGenerate = async () => {
    if (!runId) return;
    const parsedNumRows = parseInt(numRows, 10);
    if (Number.isNaN(parsedNumRows) || parsedNumRows <= 0) {
      showError('Error', 'Please enter a valid number of rows greater than 0');
      return;
    }
    
    setIsGenerating(true);
    setGenerationStep(0);
    
    const steps = [
      'Initializing data generator',
      'Understanding your data',
      'Understanding the schema',
      'Applying relational logics',
      'Generating synthetic data',
      'Applying business rules',
      'Validating generated data',
      'Saving output files'
    ];
    
    const stepInterval = setInterval(() => {
      setGenerationStep(prev => (prev + 1) % steps.length);
    }, 2000);
    
    try {
      const result = await apiService.generateData(runId, parsedNumRows);
      
      clearInterval(stepInterval);
      
      if (result && result.success) {
        const fileCount = result.output_files?.length || 0;
        showSuccess('Success', `Data generated successfully! ${fileCount} file(s) created.`);
        await loadRunData();
      } else {
        // Enhanced error display with actionable steps
        const errorDetails = result?.error || result?.stderr || 'Failed to generate data';
        const errorType = result?.error_type || 'generation_failed';
        const actionableSteps = result?.actionable_steps || [];
        const stderr = result?.stderr || '';
        const stdout = result?.stdout || '';
        
        let errorMessage = result?.error || 'Generation failed';
        if (actionableSteps.length > 0) {
          errorMessage += '\n\nWhat you can do:\n' + actionableSteps.map((step: string, idx: number) => `${idx + 1}. ${step}`).join('\n');
        }
        if (stderr) {
          errorMessage += `\n\nError Details:\n${stderr.substring(0, 1000)}${stderr.length > 1000 ? '...' : ''}`;
        }
        
        showError('Generation Failed', errorMessage);
      }
    } catch (error) {
      clearInterval(stepInterval);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate data';
      showError('Generation Failed', `An error occurred during generation:\n\n${errorMessage}\n\nPlease try again or check the console for more details.`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Code Viewer
  const handleShowCode = async () => {
    if (!runId) return;
    
    setIsLoadingCode(true);
    setShowCodeViewer(true);
    
    try {
      const result = await apiService.getGeneratorCode(runId);
      if (result && result.code) {
        setGeneratorCode(result.code);
      } else {
        setGeneratorCode('// No generator code found. Generate code from the Table Selection page first.');
      }
    } catch (error) {
      setGeneratorCode(`// Error loading code: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingCode(false);
    }
  };

  // Column EDA
  const handleColumnEDA = async (tableName: string, columnName: string) => {
    if (!runId) return;
    
    setIsLoadingEDA(true);
    setShowEDAModal(true);
    setEdaTableName(tableName);
    setColumnEDA(null);
    
    try {
      const result = await apiService.getColumnEDA(runId, tableName, columnName);
      setColumnEDA(result);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to load column EDA');
      setShowEDAModal(false);
    } finally {
      setIsLoadingEDA(false);
    }
  };

  // Preview/Edit handlers
  const handlePreviewCSV = async (filename: string) => {
    if (!runId) return;
    
    try {
      const data = await apiService.previewCSV(runId, filename);
      setPreviewData(data);
      setPreviewFilename(filename);
      setIsEditMode(false);
      setEditedData([]);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to load CSV preview');
    }
  };

  const handleEditCSV = async (filename: string, page: number = 1) => {
    if (!runId) return;
    
    try {
      setIsLoadingPage(true);
      const data = await apiService.getFullCSV(runId, filename, page, 500);
      setPreviewData(data);
      setPreviewFilename(filename);
      setEditedData(data.data || []);
      setCurrentPage(page);
      setIsEditMode(true);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to load CSV for editing');
    } finally {
      setIsLoadingPage(false);
    }
  };
  
  const handlePageChange = async (newPage: number) => {
    if (previewFilename && isEditMode) {
      await handleEditCSV(previewFilename, newPage);
    }
  };

  const handleSaveCSV = async () => {
    if (!runId || !previewFilename || editedData.length === 0) return;
    
    setIsSaving(true);
    try {
      const result = await apiService.saveCSV(runId, previewFilename, editedData);
      if (result && result.success) {
        showSuccess('Success', `File ${previewFilename} saved successfully!`);
        setIsEditMode(false);
        await handlePreviewCSV(previewFilename);
      } else {
        showError('Error', result?.error || 'Failed to save file');
      }
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to save CSV');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadCSV = async (filename: string) => {
    if (!runId) return;
    
    setIsDownloading(true);
    try {
      await apiService.downloadCSV(runId, filename);
      showSuccess('Success', `Downloaded ${filename}`);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!runId) return;
    
    setIsDownloading(true);
    try {
      await apiService.downloadAllCSV(runId);
      showSuccess('Success', 'All files downloaded as ZIP');
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to download files');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadXML = async (filename: string) => {
    if (!runId) return;

    setIsDownloading(true);
    try {
      await apiService.downloadXML(runId, filename);
      showSuccess('Success', `Downloaded ${filename.replace(/\.csv$/i, '.xml')}`);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to download XML file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAllXML = async () => {
    if (!runId) return;

    setIsDownloading(true);
    try {
      await apiService.downloadAllXML(runId);
      showSuccess('Success', 'All table XML files downloaded as ZIP');
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to download XML files');
    } finally {
      setIsDownloading(false);
    }
  };


  const handleOpenExportModal = (type: 'mysql' | 'postgresql' | 'oracle' | 'sqlserver') => {
    const defaultPorts: Record<string, string> = {
      mysql: '3306',
      postgresql: '5432',
      oracle: '1521',
      sqlserver: '1433'
    };
    
    setExportDbType(type);
    setExportConnection({
      type,
      host: 'localhost',
      port: defaultPorts[type],
      database: '',
      username: '',
      password: '',
      schema: ''
    });
    setExportConnectionStatus('idle');
    setShowExportModal(true);
  };

  const handleTestExportConnection = async () => {
    setExportConnectionStatus('testing');
    
    try {
      const response = await apiService.testDatabaseConnection(exportConnection);
      
      if (response.success) {
        setExportConnectionStatus('success');
        showSuccess('Connection Successful', 'Database connection test passed!');
      } else {
        setExportConnectionStatus('error');
        showError('Connection Failed', response.error || 'Could not connect to database');
      }
    } catch (error) {
      setExportConnectionStatus('error');
      showError('Connection Failed', error instanceof Error ? error.message : 'Could not connect to database');
    }
  };

  const handleExportToDatabase = async () => {
    if (!runId) return;
    
    if (!exportConnection.host || !exportConnection.database || !exportConnection.username) {
      showError('Error', 'Please fill in all required fields');
      return;
    }

    setIsExporting(true);
    setExportStep(0);
    setShowExportModal(false);

    const steps = [
      'Connecting to database',
      'Reading CSV files',
      'Creating tables',
      'Inserting data',
      'Validating export',
      'Finalizing'
    ];

    const stepInterval = setInterval(() => {
      setExportStep(prev => (prev + 1) % steps.length);
    }, 1500);

    try {
      const result = await apiService.exportToDatabase(runId, exportConnection);
      
      clearInterval(stepInterval);
      setIsExporting(false);
      
      if (result && result.success) {
        showSuccess('Success', `Exported ${result.num_tables || 0} tables to ${exportDbType}`);
      } else {
        showError('Error', result?.error || 'Failed to export to database');
      }
    } catch (error) {
      clearInterval(stepInterval);
      setIsExporting(false);
      console.error('Failed to export to database:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to export to database');
    }
  };

  const getDbIcon = (type: string) => {
    switch (type) {
      case 'mysql':
        return <Database className="w-6 h-6 text-orange-600" />;
      case 'postgresql':
        return <Database className="w-6 h-6 text-blue-600" />;
      case 'oracle':
        return <Database className="w-6 h-6 text-red-600" />;
      case 'sqlserver':
        return <Server className="w-6 h-6 text-purple-600" />;
      default:
        return <Database className="w-6 h-6 text-gray-600" />;
    }
  };

  const getDbColor = (type: string) => {
    switch (type) {
      case 'mysql': return 'bg-orange-100 dark:bg-orange-900/30';
      case 'postgresql': return 'bg-blue-100 dark:bg-blue-900/30';
      case 'oracle': return 'bg-red-100 dark:bg-red-900/30';
      case 'sqlserver': return 'bg-purple-100 dark:bg-purple-900/30';
      default: return 'bg-gray-100 dark:bg-gray-900/30';
    }
  };

  const startEditing = (rowIndex: number, column: string) => {
    setEditingCell({ rowIndex, column });
  };

  const saveEdit = (rowIndex: number, column: string, value: string) => {
    const updatedData = [...editedData];
    updatedData[rowIndex] = { ...updatedData[rowIndex], [column]: value };
    setEditedData(updatedData);
    setEditingCell(null);
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, rowIndex: number, column: string, value: string) => {
    if (e.key === 'Enter') {
      saveEdit(rowIndex, column, value);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // Get foreign keys for current table
  const getForeignKeysForTable = (tableName: string) => {
    if (!runData?.schema?.relationships?.foreign_keys) return [];
    return runData.schema.relationships.foreign_keys.filter(
      (fk: any) => fk.child_table === tableName
    );
  };

  const syntheticFiles = runData?.synthetic_csv_files || [];
  const schema = runData?.schema;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <main className="p-6" style={{ minHeight: 'calc(100vh - 72px)' }}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Generate Synthetic Data</h1>
              <p className="text-gray-600 mt-1">
                Configure business rules and generate synthetic data
              </p>
            </div>
            <button
              onClick={() => navigate(`/run/${runId}/select`)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-800 flex items-center space-x-2 transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Back to Table Selection</span>
            </button>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Business Rules */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span>Business Rules (Optional)</span>
                </h2>
              </div>
              <div className="p-4">
                {/* Smart Suggestions Section */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Quick Edge Case Suggestions:
                    </label>
                    <button
                      onClick={() => setShowPurposeInput(!showPurposeInput)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                    >
                      <MessageSquare size={14} />
                      <span>Describe Data Purpose</span>
                    </button>
                  </div>
                  
                  {/* Purpose Input */}
                  {showPurposeInput && (
                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-blue-800 dark:text-blue-200">
                          What is this data for? (helps generate better suggestions)
                        </label>
                        <button
                          onClick={(e) => {
                            if (!isPurposeLocked) {
                              // Only lock if not already locked
                              setIsPurposeLocked(true);
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            setIsPurposeLocked(false);
                            setShowPurposeInput(true);
                          }}
                          className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded transition-colors"
                          title={isPurposeLocked ? "Double-click to edit" : "Click to lock"}
                        >
                          {isPurposeLocked ? (
                            <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                          ) : (
                            <CheckCircle size={16} className="text-gray-400 dark:text-gray-500" />
                          )}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={dataPurpose}
                        onChange={(e) => setDataPurpose(e.target.value)}
                        placeholder="e.g., Hospital patient management system, E-commerce order tracking..."
                        disabled={isPurposeLocked}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                          isPurposeLocked 
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-700 cursor-not-allowed' 
                            : 'border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100'
                        }`}
                      />
                      {isPurposeLocked && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Context locked. Double-click the checkmark to edit.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Suggestions Dropdown */}
                  <div className="relative">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          if (showSuggestions && suggestions) {
                            setShowSuggestions(false);
                          } else {
                            handleLoadSuggestions(dataPurpose);
                          }
                        }}
                        disabled={isLoadingSuggestions}
                        className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-between transition-colors bg-white dark:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          {isLoadingSuggestions ? (
                            <Loader2 size={16} className="animate-spin text-blue-600" />
                          ) : (
                            <Sparkles size={16} className="text-amber-500" />
                          )}
                          <span className="text-gray-700 dark:text-gray-300">
                            {isLoadingSuggestions ? 'Loading AI suggestions...' : 'Click for AI-powered test case suggestions'}
                          </span>
                        </div>
                        <ChevronDown size={16} className="text-gray-400" />
                      </button>
                      {cachedSuggestions && (
                        <button
                          onClick={() => handleLoadSuggestions(dataPurpose, true)}
                          className="px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
                          title="Refresh suggestions"
                        >
                          <RefreshCw size={16} className="text-gray-600 dark:text-gray-400" />
                        </button>
                      )}
                    </div>
                    
                    {/* Suggestions Panel */}
                    {showSuggestions && suggestions && (
                      <div className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
                        <div className="p-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky top-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-1">
                              {suggestions.ai_powered && <Sparkles size={14} className="text-amber-500" />}
                              <span>{suggestions.ai_powered ? 'AI-Powered Suggestions' : 'Common Test Cases'}</span>
                            </span>
                            <button
                              onClick={() => {
                                setShowSuggestions(false);
                                setSelectedSuggestions(new Set());
                              }}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <X size={16} />
                            </button>
                          </div>
                          
                          {/* Tabs */}
                          <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700">
                            <button
                              onClick={() => setActiveSuggestionTab('all')}
                              className={`px-3 py-1 text-xs font-medium rounded-t transition-colors ${
                                activeSuggestionTab === 'all'
                                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                              }`}
                            >
                              All
                            </button>
                            {suggestions.context_suggestions?.length > 0 && (
                              <button
                                onClick={() => setActiveSuggestionTab('tailored')}
                                className={`px-3 py-1 text-xs font-medium rounded-t transition-colors ${
                                  activeSuggestionTab === 'tailored'
                                    ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border-b-2 border-amber-600'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                              >
                                Tailored
                              </button>
                            )}
                            {suggestions.suggestions?.map((category: any, idx: number) => (
                              <button
                                key={idx}
                                onClick={() => setActiveSuggestionTab(category.category)}
                                className={`px-3 py-1 text-xs font-medium rounded-t transition-colors ${
                                  activeSuggestionTab === category.category
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                              >
                                {category.category}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto">
                          {/* Context-specific AI suggestions */}
                          {(activeSuggestionTab === 'all' || activeSuggestionTab === 'tailored') && suggestions.context_suggestions?.length > 0 && (
                            <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase mb-2 flex items-center space-x-1">
                                <Sparkles size={12} />
                                <span>Tailored for Your Data</span>
                              </p>
                              <div className="space-y-1">
                                {suggestions.context_suggestions.map((s: any, i: number) => (
                                  <label
                                    key={i}
                                    className="flex items-start space-x-2 px-3 py-2 text-sm rounded hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors cursor-pointer group"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedSuggestions.has(i)}
                                      onChange={() => toggleSuggestion(i)}
                                      className="mt-0.5"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">{s.category}</span>
                                      <p className="text-gray-700 dark:text-gray-300 group-hover:text-amber-800 dark:group-hover:text-amber-300">{s.prompt}</p>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Common edge case categories */}
                          {suggestions.suggestions?.map((category: any, i: number) => {
                            if (activeSuggestionTab !== 'all' && activeSuggestionTab !== category.category) return null;
                            
                            let indexOffset = suggestions.context_suggestions?.length || 0;
                            for (let j = 0; j < i; j++) {
                              indexOffset += suggestions.suggestions[j].suggestions.length;
                            }
                            
                            return (
                              <div key={i} className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">{category.category}</p>
                                <div className="space-y-1">
                                  {category.suggestions.map((prompt: string, j: number) => {
                                    const globalIndex = indexOffset + j;
                                    return (
                                      <label
                                        key={j}
                                        className="flex items-start space-x-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedSuggestions.has(globalIndex)}
                                          onChange={() => toggleSuggestion(globalIndex)}
                                          className="mt-0.5"
                                        />
                                        <span className="flex-1">{prompt}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Finalize Button */}
                        {selectedSuggestions.size > 0 && (
                          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky bottom-0">
                            <button
                              onClick={addSelectedSuggestions}
                              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                            >
                              Add {selectedSuggestions.size} Selected Rule{selectedSuggestions.size > 1 ? 's' : ''}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Describe constraints in natural language:
                </label>
                <textarea
                  value={constraints}
                  onChange={(e) => setConstraints(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  rows={6}
                  placeholder="Example: All patients must be 18 years or older. Doctors can only have specialties from Cardiology, Neurology, or Sports Medicine."
                />
                <p className="text-sm text-gray-500 mt-2">
                  These rules will modify the data generator code.
                </p>
                <button
                  onClick={handleApplyRules}
                  disabled={!constraints.trim() || isApplying}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isApplying ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <FileText size={16} />
                      <span>Apply Rules</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Right: Generate Data */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                  <Play className="h-5 w-5 text-green-600" />
                  <span>Generate Data</span>
                </h2>
              </div>
              <div className="p-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number of rows per table:
                </label>
                <input
                  type="number"
                  value={numRows}
                  onChange={(e) => setNumRows(e.target.value)}
                  max={10000}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
                <div className="mt-4 flex items-center space-x-3">
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        <span>Generate Data</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={loadRunData}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-2 bg-white dark:bg-gray-900"
                  >
                    <RefreshCw size={16} />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Generated Tables */}
          {syntheticFiles.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                  <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span>Generated Tables ({syntheticFiles.length})</span>
                </h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleDownloadAll}
                    disabled={isDownloading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    {isDownloading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <DownloadCloud size={16} />
                    )}
                    <span>Download All</span>
                  </button>
                  <button
                    onClick={handleDownloadAllXML}
                    disabled={isDownloading}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center space-x-2 bg-white dark:bg-gray-900"
                  >
                    <DownloadCloud size={16} />
                    <span>Download All XML</span>
                  </button>
                  <button
                    onClick={() => navigate(`/run/${runId}/export`)}
                    disabled={isExporting}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    <Upload size={16} />
                    <span>Export to Database</span>
                  </button>
                  <button
                    type="button"
                    disabled
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed flex items-center space-x-2"
                    title="Coming soon"
                  >
                    <Server size={16} />
                    <span>Serve data as API (Coming soon)</span>
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {syntheticFiles.map((filename: string) => (
                    <div
                      key={filename}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-colors bg-white dark:bg-gray-800"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">{filename}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePreviewCSV(filename)}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-1 text-sm bg-white dark:bg-gray-900"
                        >
                          <Eye size={14} />
                          <span>Preview</span>
                        </button>
                        <button
                          onClick={() => handleEditCSV(filename)}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-1 text-sm bg-white dark:bg-gray-900"
                        >
                          <Edit2 size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDownloadCSV(filename)}
                          disabled={isDownloading}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-1 text-sm bg-white dark:bg-gray-900"
                        >
                          <Download size={14} />
                          <span>Download</span>
                        </button>
                        <button
                          onClick={() => handleDownloadXML(filename)}
                          disabled={isDownloading}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-1 text-sm bg-white dark:bg-gray-900"
                        >
                          <Download size={14} />
                          <span>XML</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {syntheticFiles.length === 0 && !isGenerating && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Generated Yet</h3>
              <p className="text-gray-600">
                Click "Generate Data" to create synthetic data for your selected tables
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Floating Code Viewer Button */}
      <button
        onClick={handleShowCode}
        className="fixed bottom-6 right-6 px-4 py-3 bg-gray-800 dark:bg-gray-700 text-white dark:text-gray-100 rounded-lg shadow-lg hover:bg-gray-900 dark:hover:bg-gray-600 flex items-center space-x-2 z-40"
      >
        <Code size={20} />
        <span>View Generator Code</span>
      </button>

      {/* Code Viewer Modal */}
      {showCodeViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                <Code className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <span>Generator Python Code</span>
              </h2>
              <button
                onClick={() => setShowCodeViewer(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-900 dark:bg-gray-950">
              {isLoadingCode ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-white dark:text-gray-300 animate-spin" />
                </div>
              ) : (
                <pre className="text-sm text-gray-100 dark:text-gray-200 font-mono whitespace-pre-wrap">
                  {generatorCode}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Column EDA Modal */}
      {showEDAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <span>Column Analysis</span>
              </h2>
              <button
                onClick={() => setShowEDAModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {isLoadingEDA ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
              ) : columnEDA ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.column_name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{edaTableName}</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium">
                      {columnEDA.data_type}
                    </span>
                  </div>

                  {/* Basic Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Values</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.total_count.toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Unique Values</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.unique_count.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{columnEDA.unique_percentage}%</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Null Values</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.null_count.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{columnEDA.null_percentage}%</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Non-Null</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.non_null_count.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Numeric Stats */}
                  {columnEDA.is_numeric && (
                    <>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Statistical Summary</h4>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Min</p>
                            <p className="text-lg font-bold text-blue-900 dark:text-blue-300 truncate" title={`Minimum value: ${columnEDA.min?.toLocaleString()}`}>
                              {columnEDA.min?.toLocaleString()}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Minimum value: {columnEDA.min?.toLocaleString()}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Max</p>
                            <p className="text-lg font-bold text-blue-900 dark:text-blue-300 truncate" title={`Maximum value: ${columnEDA.max?.toLocaleString()}`}>
                              {columnEDA.max?.toLocaleString()}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Maximum value: {columnEDA.max?.toLocaleString()}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center overflow-hidden group relative">
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">Mean</p>
                            <p className="text-lg font-bold text-green-900 dark:text-green-300 truncate" title={`Mean (average): ${columnEDA.mean?.toFixed(2)}`}>
                              {columnEDA.mean?.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Mean (average): {columnEDA.mean?.toFixed(2)}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center overflow-hidden group relative">
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">Median</p>
                            <p className="text-lg font-bold text-green-900 dark:text-green-300 truncate" title={`Median (middle value): ${columnEDA.median?.toFixed(2)}`}>
                              {columnEDA.median?.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Median (middle value): {columnEDA.median?.toFixed(2)}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Std Dev</p>
                            <p className="text-lg font-bold text-purple-900 dark:text-purple-300 truncate" title={`Standard deviation: ${columnEDA.std?.toFixed(2)}`}>
                              {columnEDA.std?.toFixed(2)}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Standard deviation: {columnEDA.std?.toFixed(2)}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Mode</p>
                            <p className="text-lg font-bold text-purple-900 dark:text-purple-300 truncate" title={`Mode (most frequent): ${columnEDA.mode && columnEDA.mode.length > 0 ? columnEDA.mode[0] : 'N/A'}`}>
                              {columnEDA.mode && columnEDA.mode.length > 0 ? columnEDA.mode[0] : 'N/A'}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Mode (most frequent): {columnEDA.mode && columnEDA.mode.length > 0 ? columnEDA.mode[0] : 'N/A'}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Percentiles */}
                      {columnEDA.percentiles && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Percentiles</h4>
                          <div className="flex items-center space-x-1">
                            {Object.entries(columnEDA.percentiles).map(([key, value]) => (
                              <div key={key} className="flex-1 bg-gray-100 dark:bg-gray-700 rounded p-2 text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">{key.toUpperCase()}</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value.toFixed(2)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Distribution (Simple Bar) */}
                      {columnEDA.histogram && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Distribution</h4>
                          <div className="relative bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" style={{ minHeight: '160px' }}>
                            {/* Y-axis grid lines - behind everything */}
                            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" style={{ padding: '12px', zIndex: 0 }}>
                              {[0, 25, 50, 75, 100].map((percent) => (
                                <div
                                  key={percent}
                                  className="w-full border-t border-gray-300 dark:border-gray-600 opacity-30"
                                  style={{ marginTop: percent === 0 ? '0' : percent === 100 ? 'auto' : 'auto' }}
                                />
                              ))}
                            </div>
                            {/* Y-axis labels - behind bars */}
                            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pointer-events-none" style={{ padding: '12px 4px', zIndex: 1 }}>
                              {(() => {
                                const maxCount = Math.max(...columnEDA.histogram!.counts);
                                return [100, 75, 50, 25, 0].map((percent) => (
                                  <span key={percent}>{Math.round((maxCount * percent) / 100)}</span>
                                ));
                              })()}
                            </div>
                            {/* Bars - on top */}
                            <div className="flex items-end space-x-1 h-32 ml-8 relative" style={{ zIndex: 10 }}>
                              {columnEDA.histogram.counts.map((count, idx) => {
                                const maxCount = Math.max(...columnEDA.histogram!.counts);
                                const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                const binStart = columnEDA.histogram!.bin_edges[idx];
                                const binEnd = columnEDA.histogram!.bin_edges[idx + 1];
                                const binRange = binEnd !== undefined 
                                  ? `${binStart.toFixed(2)} - ${binEnd.toFixed(2)}`
                                  : `${binStart.toFixed(2)}+`;
                                const percentage = maxCount > 0 ? ((count / maxCount) * 100).toFixed(1) : '0';
                                
                                return (
                                  <div
                                    key={idx}
                                    className="flex-1 group relative"
                                    style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}
                                  >
                                    <div
                                      className="w-full bg-blue-500 dark:bg-blue-600 rounded-t hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors cursor-pointer"
                                      style={{ 
                                        height: `${height}%`, 
                                        minHeight: height > 0 ? '2px' : '0',
                                        width: '100%'
                                      }}
                                    />
                                    {/* Enhanced tooltip */}
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                      <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
                                        <div className="font-semibold mb-1">Bin {idx + 1}</div>
                                        <div>Range: {binRange}</div>
                                        <div>Count: {count.toLocaleString()}</div>
                                        <div>Percentage: {percentage}%</div>
                                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                          <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* X-axis label */}
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 ml-8">
                              Value Range (10 bins)
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Skewness & Kurtosis */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Skewness</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.skewness?.toFixed(3)}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {(columnEDA.skewness || 0) > 0.5 ? 'Right-skewed' : 
                             (columnEDA.skewness || 0) < -0.5 ? 'Left-skewed' : 'Approximately symmetric'}
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Kurtosis</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.kurtosis?.toFixed(3)}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {(columnEDA.kurtosis || 0) > 0 ? 'Heavy-tailed' : 'Light-tailed'}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Categorical Stats */}
                  {!columnEDA.is_numeric && columnEDA.top_values && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Top Values</h4>
                      <div className="space-y-2">
                        {columnEDA.top_values.slice(0, 10).map((item, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs" title={item.value}>
                                  {item.value}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">{item.count} ({item.percentage}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-blue-500 dark:bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${item.percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-12">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSV Preview/Edit Modal */}
      {previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{previewFilename}</h2>
                {isEditMode && (
                  <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-sm">Edit Mode</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {isEditMode && (
                  <>
                    <button
                      onClick={handleSaveCSV}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-1"
                    >
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      <span>Save</span>
                    </button>
                    <button
                      onClick={() => {
                        setIsEditMode(false);
                        handlePreviewCSV(previewFilename);
                      }}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-900"
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => setPreviewData(null)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Table Info with Foreign Keys */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>Rows: {isEditMode ? editedData.length : previewData.total_rows}</span>
                  <span>Columns: {previewData.columns?.length || 0}</span>
                </div>
                
                {/* Foreign Key Info */}
                {(() => {
                  const tableName = previewFilename.replace('.csv', '').replace('_synthetic', '');
                  const foreignKeys = getForeignKeysForTable(tableName);
                  
                  if (foreignKeys.length > 0) {
                    return (
                      <div className="flex items-center space-x-2 flex-wrap gap-2">
                        <Key className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Foreign Keys:</span>
                        {foreignKeys.map((fk: any, idx: number) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs flex items-center space-x-1"
                          >
                            <Link2 size={12} />
                            <span>{fk.child_column} → {fk.parent_table}.{fk.parent_column}</span>
                          </span>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
            
            <div className="flex-1 overflow-auto">
              <table className="w-full">
                <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0 z-10">
                  <tr>
                    {previewData.columns?.map((col: string) => {
                      const tableName = previewFilename.replace('.csv', '').replace('_synthetic', '');
                      
                      // Get column info from schema to check nullable status
                      const getColumnInfo = () => {
                        if (!schema?.tables) return null;
                        for (const table of schema.tables) {
                          if (table.table_name === tableName || table.name === tableName) {
                            const column = table.columns?.find((c: any) => c.name === col);
                            return column;
                          }
                        }
                        return null;
                      };
                      
                      const columnInfo = getColumnInfo();
                      const isNullable = columnInfo?.nullable !== false; // Default to true if not specified
                      
                      return (
                        <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span>{col}</span>
                            {isNullable ? (
                              <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs font-normal" title="Nullable">
                                NULL
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-normal" title="Not Nullable">
                                NOT NULL
                              </span>
                            )}
                            <button
                              onClick={() => handleColumnEDA(tableName, col)}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                              title="View column analysis"
                            >
                              <BarChart3 size={14} className="text-blue-600 dark:text-blue-400" />
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(isEditMode ? editedData : (previewData.preview || previewData.data || []).slice(0, 100)).map((row: any, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      {previewData.columns?.map((col: string) => {
                        const isEditing = editingCell?.rowIndex === idx && editingCell?.column === col;
                        const currentValue = isEditMode ? (editedData[idx]?.[col] ?? '') : (row[col] ?? '');
                        
                        return (
                          <td key={col} className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                            {isEditMode && isEditing ? (
                              <input
                                type="text"
                                value={String(currentValue)}
                                onChange={(e) => {
                                  const updatedData = [...editedData];
                                  updatedData[idx] = { ...updatedData[idx], [col]: e.target.value };
                                  setEditedData(updatedData);
                                }}
                                onKeyDown={(e) => handleCellKeyDown(e, idx, col, String(currentValue))}
                                onBlur={() => saveEdit(idx, col, String(currentValue))}
                                className="w-full p-1 border border-blue-400 dark:border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                autoFocus
                              />
                            ) : (
                              <div
                                className={`truncate ${isEditMode ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 p-1 rounded' : ''}`}
                                title={String(currentValue)}
                                onClick={() => isEditMode && startEditing(idx, col)}
                              >
                                {String(currentValue)}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {isEditMode && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border-t border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Tip:</strong> Click on any cell to edit. Press Enter to save, Escape to cancel.
                  </p>
                  
                  {/* Pagination Controls */}
                  {previewData.pagination && (
                    <div className="flex items-center space-x-2">
                      {isLoadingPage && <Loader2 size={14} className="animate-spin text-blue-600 dark:text-blue-400" />}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!previewData.pagination.has_prev || isLoadingPage}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Page {previewData.pagination.page} of {previewData.pagination.total_pages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!previewData.pagination.has_next || isLoadingPage}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading Screens */}
      {/* Export to Database Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 ${getDbColor(exportDbType)} dark:opacity-80 rounded-lg flex items-center justify-center`}>
                    {getDbIcon(exportDbType)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 capitalize">{exportDbType} Export</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Enter your database credentials</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Database Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Database Type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleOpenExportModal('mysql')}
                    className={`p-3 border-2 rounded-lg flex items-center space-x-2 transition-all ${
                      exportDbType === 'mysql'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-orange-300 dark:hover:border-orange-500'
                    }`}
                  >
                    <Database className={`w-5 h-5 ${exportDbType === 'mysql' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">MySQL</span>
                  </button>
                  <button
                    onClick={() => handleOpenExportModal('postgresql')}
                    className={`p-3 border-2 rounded-lg flex items-center space-x-2 transition-all ${
                      exportDbType === 'postgresql'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500'
                    }`}
                  >
                    <Database className={`w-5 h-5 ${exportDbType === 'postgresql' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">PostgreSQL</span>
                  </button>
                  <button
                    onClick={() => handleOpenExportModal('oracle')}
                    className={`p-3 border-2 rounded-lg flex items-center space-x-2 transition-all ${
                      exportDbType === 'oracle'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-500'
                    }`}
                  >
                    <Database className={`w-5 h-5 ${exportDbType === 'oracle' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Oracle</span>
                  </button>
                  <button
                    onClick={() => handleOpenExportModal('sqlserver')}
                    className={`p-3 border-2 rounded-lg flex items-center space-x-2 transition-all ${
                      exportDbType === 'sqlserver'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500'
                    }`}
                  >
                    <Server className={`w-5 h-5 ${exportDbType === 'sqlserver' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">SQL Server</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Host *</label>
                  <input
                    type="text"
                    value={exportConnection.host}
                    onChange={(e) => setExportConnection({ ...exportConnection, host: e.target.value })}
                    placeholder="localhost"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port</label>
                  <input
                    type="text"
                    value={exportConnection.port}
                    onChange={(e) => setExportConnection({ ...exportConnection, port: e.target.value })}
                    placeholder={exportDbType === 'mysql' ? '3306' : exportDbType === 'postgresql' ? '5432' : exportDbType === 'oracle' ? '1521' : '1433'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Database Name *</label>
                <input
                  type="text"
                  value={exportConnection.database}
                  onChange={(e) => setExportConnection({ ...exportConnection, database: e.target.value })}
                  placeholder="my_database"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              {(exportDbType === 'postgresql' || exportDbType === 'oracle') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schema</label>
                  <input
                    type="text"
                    value={exportConnection.schema}
                    onChange={(e) => setExportConnection({ ...exportConnection, schema: e.target.value })}
                    placeholder={exportDbType === 'postgresql' ? 'public' : ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username *</label>
                <input
                  type="text"
                  value={exportConnection.username}
                  onChange={(e) => setExportConnection({ ...exportConnection, username: e.target.value })}
                  placeholder="root"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password *</label>
                <input
                  type="password"
                  value={exportConnection.password}
                  onChange={(e) => setExportConnection({ ...exportConnection, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              {/* Connection Status */}
              {exportConnectionStatus !== 'idle' && (
                <div className={`p-3 rounded-lg flex items-center space-x-2 ${
                  exportConnectionStatus === 'testing' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                  exportConnectionStatus === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                  'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                }`}>
                  {exportConnectionStatus === 'testing' && <Loader2 size={16} className="animate-spin" />}
                  {exportConnectionStatus === 'success' && <CheckCircle size={16} />}
                  {exportConnectionStatus === 'error' && <X size={16} />}
                  <span className="text-sm font-medium">
                    {exportConnectionStatus === 'testing' && 'Testing connection...'}
                    {exportConnectionStatus === 'success' && 'Connection successful!'}
                    {exportConnectionStatus === 'error' && 'Connection failed'}
                  </span>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button
                onClick={handleTestExportConnection}
                disabled={isExporting || exportConnectionStatus === 'testing'}
                className="px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50"
              >
                Test Connection
              </button>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  disabled={isExporting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExportToDatabase}
                  disabled={isExporting || !exportConnection.host || !exportConnection.database || !exportConnection.username}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                >
                  {isExporting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>Export</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProcessLoadingScreen
        isVisible={isExporting}
        title="Exporting to Database"
        description={`Exporting generated CSV files to ${exportDbType}...`}
        steps={[
          'Connecting to database',
          'Reading CSV files',
          'Creating tables',
          'Inserting data',
          'Validating export',
          'Finalizing'
        ]}
        currentStep={exportStep}
      />

      <ProcessLoadingScreen
        isVisible={isGenerating}
        title="Generating Synthetic Data"
        description="Processing your test scenarios and generating data..."
        steps={[
          'Initializing data generator',
          'Understanding your data',
          'Understanding the schema',
          'Applying relational logics',
          'Generating synthetic data',
          'Applying business rules',
          'Validating generated data',
          'Saving output files'
        ]}
        currentStep={generationStep}
      />

      <ProcessLoadingScreen
        isVisible={isApplying}
        title="Applying Business Rules"
        description="Processing your constraints and updating the data generator..."
        steps={[
          'Parsing business rules',
          'Analyzing constraints',
          'Updating data generator code',
          'Validating rule syntax',
          'Applying business logic'
        ]}
        currentStep={applyStep}
      />
    </div>
  );
};

export default GeneratePage;

